import type { ColumnDef, OnChangeFn, PaginationState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "@/components/admin/DataTable";
import { SegmentedTabs, SegTab } from "@/components/admin/SegmentedTabs";
import {
  useDeskReviewSummary,
  useDeskReviews,
  useModerateDeskReview,
} from "@/hooks/admin/deskReviews";
import { AdminLayout } from "@/layouts/AdminLayout";
import { cn } from "@/lib/utils";
import type { AdminDeskReview, DeskReviewAction, DeskReviewModerationStatus } from "@/types";

const PAGE_SIZE = 25;

const TABS: { status: DeskReviewModerationStatus; label: string }[] = [
  { status: "pending", label: "Pending" },
  { status: "published", label: "Published" },
  { status: "declined", label: "Declined" },
  { status: "unpublished", label: "Unpublished" },
];

/** The buttons each status offers; a declined review is final. */
const ACTIONS: Record<
  DeskReviewModerationStatus,
  { action: DeskReviewAction; label: string; primary?: boolean }[]
> = {
  pending: [
    { action: "approve", label: "Approve", primary: true },
    { action: "decline", label: "Decline" },
  ],
  published: [{ action: "unpublish", label: "Unpublish" }],
  unpublished: [{ action: "republish", label: "Re-publish" }],
  declined: [],
};

const DONE: Record<DeskReviewAction, string> = {
  approve: "Review published",
  decline: "Review declined",
  unpublish: "Review unpublished",
  republish: "Review re-published",
};

function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?"
  );
}

function Professional({ review }: { review: AdminDeskReview }) {
  const subtitle = [review.role, review.firm].filter(Boolean).join(" · ");
  return (
    <div className="flex items-center gap-3">
      {review.photo_url ? (
        <img
          src={review.photo_url}
          alt=""
          className="h-11 w-11 flex-shrink-0 rounded-full object-cover"
        />
      ) : (
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#e9ece8] font-semibold text-gray-700 text-sm">
          {initials(review.name)}
        </div>
      )}
      <div className="min-w-0">
        <div className="font-bold text-gray-900">{review.name}</div>
        {subtitle && <div className="text-gray-500 text-sm">{subtitle}</div>}
      </div>
    </div>
  );
}

export default function AdminDeskReviewsPage() {
  const [status, setStatus] = useState<DeskReviewModerationStatus>("pending");
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState<string | null>(null);

  const summary = useDeskReviewSummary();
  const reviews = useDeskReviews({ status, page, page_size: PAGE_SIZE });
  const moderate = useModerateDeskReview();
  const canModerate = summary.data?.can_moderate ?? false;
  const approvers = summary.data?.approvers ?? [];

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  const { mutate } = moderate;
  const act = useCallback(
    (id: string, action: DeskReviewAction) =>
      mutate(
        { id, action },
        {
          onSuccess: () => setNotice(DONE[action]),
          onError: (e) => setNotice(e instanceof Error ? e.message : "Could not update the review"),
        }
      ),
    [mutate]
  );

  const columns = useMemo<ColumnDef<AdminDeskReview>[]>(
    () => [
      {
        id: "professional",
        header: "Professional",
        enableSorting: false,
        cell: ({ row }) => <Professional review={row.original} />,
      },
      {
        accessorKey: "rating",
        header: "Rating",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-gray-900">{row.original.rating} / 5</span>
        ),
      },
      {
        accessorKey: "comment",
        header: "Review",
        enableSorting: false,
        cell: ({ row }) =>
          row.original.comment ? (
            <span className="text-gray-700">{`“${row.original.comment}”`}</span>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      {
        id: "report",
        header: "Report",
        enableSorting: false,
        cell: ({ row }) =>
          row.original.report ? (
            <Link
              to={`/admin/reviews/${row.original.report.review_id}`}
              className="whitespace-nowrap font-medium text-[#1E6F4F] hover:underline"
            >
              {`Report · ${row.original.report.score} →`}
            </Link>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      {
        id: "action",
        header: "Action",
        enableSorting: false,
        cell: ({ row }) =>
          canModerate ? (
            <div className="flex gap-2">
              {ACTIONS[row.original.status].map(({ action, label, primary }) => (
                <button
                  key={action}
                  type="button"
                  disabled={moderate.isPending}
                  onClick={() => act(row.original.id, action)}
                  className={cn(
                    "min-h-10 rounded-lg px-4 font-semibold text-sm transition-colors disabled:opacity-50",
                    primary
                      ? "bg-[#1E6F4F] text-white hover:bg-[#185d42]"
                      : "border border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null,
      },
    ],
    [canModerate, moderate.isPending, act]
  );

  const pagination: PaginationState = { pageIndex: page - 1, pageSize: PAGE_SIZE };
  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPage(next.pageIndex + 1);
  };
  const error = reviews.isError
    ? reviews.error instanceof Error
      ? reviews.error.message
      : "Failed to load reviews"
    : null;

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        {!canModerate && summary.isSuccess && (
          <p className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-600 text-sm">
            You can view reviews; only approvers can change their status.
          </p>
        )}
        <DataTable
          className="h-full"
          columns={columns}
          data={reviews.data?.items ?? []}
          isLoading={reviews.isPending}
          error={error}
          onRetry={() => reviews.refetch()}
          emptyMessage={`No ${status} reviews`}
          getRowId={(r) => r.id}
          toolbar={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SegmentedTabs className="bg-[#e9ece8]">
                {TABS.map((tab) => (
                  <SegTab
                    key={tab.status}
                    active={status === tab.status}
                    onClick={() => {
                      setStatus(tab.status);
                      setPage(1);
                    }}
                  >
                    {`${tab.label} · ${summary.data?.counts[tab.status] ?? 0}`}
                  </SegTab>
                ))}
              </SegmentedTabs>
              <span className="text-gray-500 text-sm">
                {approvers.length
                  ? `Approvers: ${approvers.join(", ")} · others can view only`
                  : "Only approvers can change status"}
              </span>
            </div>
          }
          manualPagination
          pageCount={reviews.data?.total_pages ?? 0}
          rowCount={reviews.data?.total ?? 0}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
        />
      </div>
      {notice && (
        <div
          role="status"
          className="fixed right-6 bottom-6 z-50 rounded-lg bg-gray-900 px-4 py-3 text-sm text-white shadow-lg"
        >
          {notice}
        </div>
      )}
    </AdminLayout>
  );
}
